import { cloneDeep, merge } from 'lodash';
import { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { updateRestorableStoreId } from 'redux/slices/storeSlice';

import { calculateEdgeConstraints, getArrowsFromOutlines, getOutlinePts } from '@canvas-common';
import { CANVAS_OBJECT_TYPE_PREFIX, FAUX_SHAPE_INDEX } from '@common/constants';
import { ShapeType, SideItemOption } from '@common/enums';
import { DrawingTabEnum } from '@common/enums/drawing-tab.enum';
import { formatPhoneNumber } from '@common/helpers';
import {
    ArrowInfo,
    CustomerInfo,
    DrawingAppliance,
    DrawingShape,
    Estimate,
    PricedProduct,
    ProductCatalogMap,
    SelectedItemDetailEntry,
    SelectedItemProperties,
    SerializableDrawingShape,
    newEstimate,
} from '@common/models';
import { GetEstimateDto } from '@common/models/save-estimate-dto';
import { getProductDescription, isActualProduct } from '@common/utils';
import { reviver } from '@common/utils/estimate.utils';
import { getLabelsForArrows } from '@common/utils/indexToLetterLabel';
import useFetchedEstimate from '@core/hooks/useFetchedEstimate';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import {
    DrawingShapeContext,
    useOptionalProductContext,
    usePricedProductsContext,
    useProductDetailsContext,
} from '@ee-context';
import { selectSelectedChipMinimizers, updateSelectedChipMinimizers } from '@redux/slices/chipMinimizerSlice';
import { updateRestorableColor } from '@redux/slices/colorSlice';
import { selectSelectedCutouts, updateSelectedCutouts } from '@redux/slices/cutoutSlice';
import { addClearedShapeEditNotification, resetClearedShapeEditNotifications } from '@redux/slices/drawingShapeSlice';
import { selectApplySameEdge, updateApplySameEdge } from '@redux/slices/edgeSlice';
import {
    selectCurrentEstimate,
    selectValidSmallTicketNumber,
    updateCustomerData,
    updateIsFetchedCustomerData,
} from '@redux/slices/estimateSlice';
import { selectSelectedFaucets, updateSelectedFaucets } from '@redux/slices/faucetSlice';
import { updateRestorableMaterial } from '@redux/slices/materialSlice';
import { selectSelectedPlumbing, updateSelectedPlumbing } from '@redux/slices/plumbingSlice';
import { updateSelectedDrawingTab } from '@redux/slices/shapeSlice';
import { selectSelectedTearOuts, updateSelectedTearOuts } from '@redux/slices/tearOutSlice';
import { selectZipcode, updateZipcode } from '@redux/slices/zipcodeSlice';

import {
    getInitialLabelIndexFromRemovedHistory,
    getLetterStartFromDrawingEdges,
} from '../../pages/easy-estimator/components/scrollable-area/design-countertop/canvas/drawing-tool/builders/measurements-builder';
import drawingTool from '../../pages/easy-estimator/components/scrollable-area/design-countertop/drawer/drawingTool';

/**
 * LoadEstimateKeys must have either code OR (estimateNumber and email)
 * @param estimateNumber is the estimate number to fetch
 * @param email is the customer email associated with the estimate Number
 */
export interface LoadEstimateKeys {
    estimateNumber?: string;
    email?: string;
}

type useEstimateReturn = {
    updateEstimateFromDesign: (customerInfo?: Partial<CustomerInfo>) => Estimate;
    updateEstimateFromJSON: (json: string | null) => Estimate;
    restoreEstimate: (estimate: Estimate) => Estimate;
    restoreFromFetchedEstimate: (estimate: GetEstimateDto) => Estimate;
};

/*
provides access to all the contexts for building the save-able estimate
 */

// eslint-disable-next-line sonarjs/cognitive-complexity
function useEstimate(): useEstimateReturn {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const zipcode = useAppSelector(selectZipcode);
    const currentEstimate = useAppSelector(selectCurrentEstimate);
    const { selectedValue: selectedStore } = useSelectedStore();

    const {
        drawingShapes,
        updateDrawingShapes,
        updateActiveDrawingShape,
        clearDrawingShapesUndoStack,
        resetDrawingShapes,
    } = useContext(DrawingShapeContext);

    const { resetAllPricing, pricedProducts, quoteLineItems } = usePricedProductsContext();
    const applySameEdge = useAppSelector(selectApplySameEdge);
    // we handle sinks, radii, and diagonal corners differently
    const { makeRestorableOptionalProducts, setRestorableOptionalProducts, makeOptionalProductNotes } =
        useOptionalProductContext();
    const { selectedValue: selectedColor } = useSelectedColor();

    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const selectedCutouts = useAppSelector(selectSelectedCutouts);
    const selectedTearOuts = useAppSelector(selectSelectedTearOuts);
    const selectedChipMinimizers = useAppSelector(selectSelectedChipMinimizers);
    const selectedFaucets = useAppSelector(selectSelectedFaucets);
    const selectedPlumbing = useAppSelector(selectSelectedPlumbing);

    const { productCatalog, setRestorableCatalog } = useProductDetailsContext();

    const validatedTicketNumber = useAppSelector(selectValidSmallTicketNumber);

    const { fetchedEstimate } = useFetchedEstimate();

    /**
     * If user changes the selectedColor to a new prodQteGrp (i.e. qteGrpID) then clear the restorable catalog
     * as it is no longer valid and must be rebuilt for the new product group
     */
    useEffect(() => {
        if (
            fetchedEstimate?.estimateData?.qteGrpID &&
            fetchedEstimate.estimateData.qteGrpID !== selectedColor?.qteGrpID
        ) {
            setRestorableCatalog(new Map());
        }
    }, [fetchedEstimate?.estimateData?.qteGrpID, selectedColor?.qteGrpID, setRestorableCatalog]);

    /**
     * create a new Estimate object using current design selections
     */
    const updateEstimateFromDesign = useCallback(
        (customerInfo?: Partial<CustomerInfo>): Estimate => {
            const estimate = newEstimate(customerInfo);

            estimate.drawingShapes = drawingShapes.map((shape: DrawingShape) => {
                return new SerializableDrawingShape({
                    skeleton: shape.skeleton,
                    cornerMap: DrawingShape.cloneCornerMap(shape.cornerMap),
                    drawingEdges: shape.drawingEdges,
                    edge: shape.edge,
                    appliances: shape.appliances,
                    removedElementsHistory: shape.removedElementsHistory,
                    label: shape.label,
                    counterLabel: shape.counterLabel,
                    establishedLetterStartIndex: shape.establishedLetterStartIndex,
                });
            });

            estimate.color = selectedColor;
            estimate.zipCode = zipcode;
            estimate.storeId = selectedStore?.companyId ?? undefined;
            estimate.materialId = selectedMaterial?.id ?? null;
            estimate.materialDesc = selectedMaterial?.description ?? null;
            estimate.qteGrpID = selectedColor?.qteGrpID ?? null;
            estimate.applySameEdge = applySameEdge;
            estimate.cutOuts = selectedCutouts;
            estimate.tearOuts = selectedTearOuts;
            estimate.faucets = selectedFaucets;
            estimate.chipMinimizers = selectedChipMinimizers;
            estimate.optionalProducts = makeRestorableOptionalProducts();
            estimate.plumbingItems = selectedPlumbing;
            estimate.productCatalog = minimizeProductCatalog(pricedProducts, productCatalog);
            estimate.pricedProducts = [...pricedProducts];
            estimate.ticketNumber = validatedTicketNumber;

            // create a note if a validatedTicketNumber is available
            if (estimate.ticketNumber) {
                estimate.productNotes.push({
                    productDescription: t('SMALL_PROJECTS.PRODUCT_NOTE.DESCRIPTION'),
                    note: t('SMALL_PROJECTS.PRODUCT_NOTE.NOTE', { ticketNumber: estimate.ticketNumber }),
                });
            }

            // Create notes for promotional items
            pricedProducts.forEach((product) => {
                if (isActualProduct(product.productId) && product.quantity !== product.adjustedQty) {
                    estimate.productNotes.push({
                        productDescription: getProductDescription(product),
                        note: t('COMMON.PROMOTION_NOTE', { ...product }),
                    });
                }
            });

            // and notes extracted from the optionalProducts
            estimate.productNotes.push(...makeOptionalProductNotes());

            estimate.lineItems = quoteLineItems;

            return estimate;
        },
        [
            applySameEdge,
            drawingShapes,
            quoteLineItems,
            makeOptionalProductNotes,
            makeRestorableOptionalProducts,
            pricedProducts,
            productCatalog,
            selectedColor,
            selectedMaterial?.description,
            selectedMaterial?.id,
            selectedColor?.qteGrpID,
            selectedStore?.companyId,
            t,
            zipcode,
        ]
    );

    const minimizeProductCatalog = (
        includeProducts: PricedProduct[],
        fullCatalog: ProductCatalogMap
    ): ProductCatalogMap =>
        includeProducts.reduce<ProductCatalogMap>((acc, product) => {
            const foundProduct = fullCatalog.get(product.productId);
            if (foundProduct) {
                acc.set(product.productId, { ...foundProduct });
            }
            return acc;
        }, new Map());

    // TODO - remove after all old estimates with 'model' are expired
    const refactorModelToApplianceModel = (appliances: DrawingAppliance[]): DrawingAppliance[] => {
        return appliances.map((appliance) => {
            if ('model' in appliance && appliance.model) {
                const modifiedAppliance = { ...appliance, applianceModel: appliance.model };
                delete modifiedAppliance.model;
                return modifiedAppliance;
            }
            return appliance;
        });
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const buildShapeListFromSavedEstimate = (estimate: Estimate): Array<DrawingShape> => {
        const dShapes: Array<DrawingShape> = [];
        const cloneEstimate = cloneDeep(estimate); // if we don't do this, we end up with references to read only elements later
        cloneEstimate.drawingShapes?.forEach((shape, idx) => {
            if (idx === FAUX_SHAPE_INDEX) {
                // restore faux shape to get appliances out in the middle of the kitchen again
                const fShape = drawingTool.buildFauxShape();
                if (shape.appliances?.length) {
                    merge(fShape.appliances, refactorModelToApplianceModel(shape.appliances));
                }
                dShapes.push(fShape);
            } else {
                if (!shape.skeleton?.length) {
                    return;
                }

                const dShape = new DrawingShape({ skeleton: shape.skeleton, type: ShapeType.PREDEFINED_SHAPE });
                if (shape.drawingEdges) {
                    dShape.id = shape.drawingEdges[0].drawingShapeId;
                }
                dShape.canvasObjectName = CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MAIN + '_' + dShape.id;

                const outlineData = getOutlinePts(dShape.skeleton);

                const outlinePoints = outlineData?.map((item) => item.coords!);
                dShape.highlightPoints = outlinePoints;

                merge(dShape.cornerMap, shape.cornerMap);
                merge(dShape.drawingEdges, shape.drawingEdges);

                dShape.removedElementsHistory = shape.removedElementsHistory
                    ? merge({}, shape.removedElementsHistory)
                    : undefined;

                // add initial arrows, correct edge ids
                const initialArrows = getArrowsFromOutlines(
                    outlinePoints!,
                    outlinePoints!,
                    { diagonals: [], cornerIds: [] },
                    []
                );

                dShape.arrowsInfo = initialArrows.map((arrowInfo, index) => {
                    const drawingEdgeId = dShape.drawingEdges[index]?.id; //drawingEdges got new id values on load, match them up
                    let isVisibleInCanvas = true;
                    let isActiveForEdgeCalc = true;
                    // check if the edge associated with this arrow has been "removed" because it's a wall/appliance/waterfall
                    const removedEdge = dShape.removedElementsHistory?.edgesAndCorners?.find(
                        (removedItem) => drawingEdgeId === removedItem.drawingEdge.id
                    );

                    const hasSideOption =
                        removedEdge &&
                        removedEdge.drawingEdge.sideItem.depth &&
                        (removedEdge.drawingEdge.sideItem.type === SideItemOption.BACKSPLASH ||
                            removedEdge.drawingEdge.sideItem.type === SideItemOption.WATERFALL);
                    // hide the label if it's got a backsplash or waterfall
                    isVisibleInCanvas = !removedEdge || !hasSideOption;
                    // and all of these are "non-finished" edges, so remove from calculations
                    isActiveForEdgeCalc = !removedEdge;

                    const fixedArrow: ArrowInfo = {
                        ...arrowInfo,
                        drawingEdgeId,
                        isVisibleInCanvas,
                        isActiveForEdgeCalc,
                    };
                    return fixedArrow;
                });

                // use the establishedLetterStartIndex _if it was saved_ OR just figure out the best place for A
                // based on the current position of the shape
                dShape.establishedLetterStartIndex =
                    shape.establishedLetterStartIndex ??
                    getInitialLabelIndexFromRemovedHistory(dShape) ??
                    getLetterStartFromDrawingEdges(dShape);

                const labels = getLabelsForArrows(dShape.arrowsInfo, dShape.establishedLetterStartIndex);

                // map the new labels into the arrows
                dShape?.arrowsInfo?.forEach((arrowInfo: ArrowInfo, index) => {
                    arrowInfo.label = labels[index];
                });

                // removed edges don't reference the actual drawingEdge objects, so we have to fix that
                dShape.removedElementsHistory?.edgesAndCorners?.forEach((removedEdge) => {
                    const edge = dShape.drawingEdges.find(
                        (edge) => edge.id === removedEdge.drawingEdge.id && edge.sideItem
                    );
                    if (!edge) {
                        // This should REALLY never happen, so let's fire a warning at least
                        console.warn('Removed edge not found for restore in useEstimate', removedEdge);
                        return;
                    }
                    removedEdge.drawingEdge = edge;
                });

                dShape.edge = shape.edge;
                dShape.label = shape.label;
                dShape.counterLabel = shape.counterLabel;

                if (shape.appliances?.length) {
                    merge(dShape.appliances, refactorModelToApplianceModel(shape.appliances));
                }

                calculateEdgeConstraints(dShape);
                dShapes.push(dShape);
            }
        });

        return dShapes;
    };

    const restoreEstimate = useCallback(
        // eslint-disable-next-line sonarjs/cognitive-complexity
        (estimate: Estimate): Estimate => {
            clearDrawingShapesUndoStack();
            resetDrawingShapes();
            resetAllPricing();
            if (!estimate || !estimate.storeId || !estimate.qteGrpID) {
                return newEstimate();
            }

            try {
                dispatch(updateRestorableMaterial({ description: estimate.materialDesc }));

                if (estimate.cutOuts) {
                    dispatch(updateSelectedCutouts(estimate.cutOuts));
                }

                if (estimate.tearOuts) {
                    dispatch(updateSelectedTearOuts(estimate.tearOuts));
                }

                if (estimate.chipMinimizers) {
                    dispatch(updateSelectedChipMinimizers(estimate.chipMinimizers));
                }

                if (estimate.optionalProducts) {
                    for (const [key, item] of estimate.optionalProducts) {
                        if (item.quantity > 0 && item.note && !item.detailEntries?.length) {
                            //this is an old quote being opened, need to add as details
                            const entry: SelectedItemDetailEntry = { quantity: item.quantity, note: item.note };
                            const updatedItem: SelectedItemProperties = { ...item, detailEntries: [entry] };
                            estimate.optionalProducts.set(key, updatedItem);
                        }
                    }
                    setRestorableOptionalProducts(estimate.optionalProducts);
                }

                if (estimate.faucets) {
                    dispatch(updateSelectedFaucets(estimate.faucets));
                }

                if (estimate.plumbingItems) {
                    dispatch(updateSelectedPlumbing(estimate.plumbingItems));
                }

                if (estimate.color) {
                    dispatch(updateRestorableColor(estimate.color));
                }

                if (estimate.productCatalog) {
                    setRestorableCatalog(estimate.productCatalog);
                }

                dispatch(updateApplySameEdge(estimate.applySameEdge));

                const drawingShapes = buildShapeListFromSavedEstimate(estimate);
                updateDrawingShapes([...drawingShapes]);
                dispatch(resetClearedShapeEditNotifications());
                drawingShapes.forEach((shape) => dispatch(addClearedShapeEditNotification(shape.id)));
                updateActiveDrawingShape(drawingShapes[FAUX_SHAPE_INDEX + 1]);

                dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));

                return estimate;
            } catch (e) {
                return currentEstimate;
            }
        },
        [
            buildShapeListFromSavedEstimate,
            clearDrawingShapesUndoStack,
            currentEstimate,
            dispatch,
            resetAllPricing,
            resetDrawingShapes,
            setRestorableCatalog,
            setRestorableOptionalProducts,
            updateActiveDrawingShape,
            updateDrawingShapes,
        ]
    );

    const restoreFromFetchedEstimate = useCallback(
        (estimateDto: GetEstimateDto): Estimate => {
            if (!estimateDto) {
                return currentEstimate;
            }
            dispatch(updateRestorableStoreId(estimateDto.storeId));

            /**
             * If there's estimate data, attempt to restore it, hard errors just fall back to currentEstimate
             */
            const tryRestoreEstimate = () => {
                try {
                    return estimateDto.estimateData ? restoreEstimate(estimateDto.estimateData) : currentEstimate;
                } catch (e) {
                    return currentEstimate;
                }
            };

            // we want to restore the estimate _before_ updating the zip code
            const returnEstimate = tryRestoreEstimate();

            dispatch(updateZipcode(estimateDto.estimateData?.zipCode ?? estimateDto.customerZip));
            return returnEstimate;
        },
        [currentEstimate, dispatch, restoreEstimate]
    );

    /**
     * parse Estimate json back to an Estimate and then restore
     * @param json the JSON text to parse
     */
    const updateEstimateFromJSON = useCallback(
        (json: string | null): Estimate => {
            if (!json || json.length === 0) {
                return currentEstimate;
            }

            try {
                const parsed: Partial<Estimate> = JSON.parse(json, reviver);
                const estimate = merge(newEstimate(), parsed);
                return restoreEstimate(estimate);
            } catch (e) {
                return currentEstimate;
            }
        },
        [currentEstimate, restoreEstimate]
    );

    useEffect(() => {
        // if there's a fetched estimate, use that info as the defaults
        // otherwise check if there's locally remembered data
        if (fetchedEstimate?.estimateNumber) {
            const fetchedCustomerData: CustomerInfo = {
                address1: fetchedEstimate.customerAddress1,
                address2: fetchedEstimate.customerAddress2,
                city: fetchedEstimate.customerCity,
                customerName: fetchedEstimate.customerName,
                email: fetchedEstimate.customerEmail,
                phone: formatPhoneNumber(fetchedEstimate.customerPhone),
                alternatePhone: formatPhoneNumber(fetchedEstimate.customerAlternatePhone),
                state: fetchedEstimate.customerState,
                zip: fetchedEstimate.customerZip,
                sendCustomerEmail: fetchedEstimate.estimateData?.customerInfo?.sendCustomerEmail ?? true,
            };
            dispatch(updateCustomerData(fetchedCustomerData));
            // THIS IS TEMPORARILY DISABLED PER EZE-557 - everybody gets to edit!
            dispatch(updateIsFetchedCustomerData(true));
        }
    }, [dispatch, fetchedEstimate]);

    return {
        updateEstimateFromDesign,
        updateEstimateFromJSON,
        restoreEstimate,
        restoreFromFetchedEstimate,
    };
}

export default useEstimate;
